import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    CheckCircle2, 
    ExternalLink, 
    Download, 
    AlertCircle,
    Copy,
    Check,
    Award,
    X,
    Sparkles,
    Loader2,
    Image as ImageIcon,
    Share2,
    ZoomIn,
    Lock,
    ArrowRight
} from 'lucide-react';
import { studentService } from '@/services/student/studentService';
import { useStudentTheme } from '@/context/StudentThemeContext';
import PdfThumbnail from '@/pages/Public/PdfThumbnail';
import toast from 'react-hot-toast';
import * as pdfjsLib from 'pdfjs-dist';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '@/pages/Public/VerifyCertificate.css';

// Configure PDF.js worker
if (typeof window !== 'undefined' && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
    try {
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version || '4.10.38'}/build/pdf.worker.min.mjs`;
    } catch {
        // fallback
    }
}
import { LinkedinIcon, TwitterIcon, InstagramIcon } from '@/components/common/SocialIcons';

interface SkillProfileData {
    id: string;
    certificate_number: string;
    student_name: string;
    enrollment_number: string;
    cohort_name: string;
    school_name?: string | null;
    completion_date: string;
    issued_at: string | null;
    certificate_url: string | null;
    certificate_image_url: string | null;
    profile_picture: string | null;
    project: {
        project_title: string | null;
        project_description: string | null;
        school_name?: string | null;
        demo_presentation_path: string | null;
        demo_presentation_name: string | null;
        project_screenshot_path?: string | null;
        project_screenshot_name?: string | null;
    };
    verified: boolean;
    is_cohort_1: boolean;
    skill_gains?: {
        presentation?: number | null;
        problem_identification?: number | null;
        problem_solving?: number | null;
        coding_skill?: number | null;
        journey_score?: number | null;
    } | null;
    is_locked?: boolean;
    message?: string;
    profile_completion?: {
        percentage: number;
        is_complete: boolean;
        sections: {
            basic_details: boolean;
            school_contact: boolean;
            academic_profile: boolean;
            profile_picture: boolean;
            project_upload?: boolean;
        };
    };
}

interface SkillItem {
    key: string;
    label: string;
    score: number;
    color: string;
}

const getLevel = (score: number): string => {
    if (score >= 85) return 'Advanced';
    if (score >= 70) return 'Proficient';
    if (score >= 50) return 'Developing';
    return 'Beginner';
};

export const formatScore = (val: number | null | undefined): string => {
    if (val == null || isNaN(val)) return '0';
    const rounded = Math.round((val + Number.EPSILON) * 10) / 10;
    return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
};

const getFileUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const baseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050/api/v1').replace('/api/v1', '');
    return `${baseUrl}/${path.startsWith('/') ? path.slice(1) : path}`;
};

const StudentSkillProfilePage: React.FC = () => {
    let studentTheme: 'light' | 'dark' = 'light';
    try {
        const themeCtx = useStudentTheme();
        studentTheme = themeCtx.theme;
    } catch {
        // fallback if mounted outside StudentThemeProvider
    }
    const theme = studentTheme;
    const navigate = useNavigate();

    const [data, setData] = useState<SkillProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [certImgFailed, setCertImgFailed] = useState(false);

    type SharePlatform = 'linkedin' | 'twitter' | 'instagram';
    const [showShareModal, setShowShareModal] = useState(false);
    const [activeShareTab, setActiveShareTab] = useState<SharePlatform>('linkedin');
    const [captionCopied, setCaptionCopied] = useState(false);
    const [twitterCopied, setTwitterCopied] = useState(false);
    const [instagramCopied, setInstagramCopied] = useState(false);
    const [downloadingImage, setDownloadingImage] = useState(false);
    const [downloadingScreenshot, setDownloadingScreenshot] = useState(false);
    const [linkedInPostContent, setLinkedInPostContent] = useState<string | null>(null);
    const [loadingLinkedInPost, setLoadingLinkedInPost] = useState(false);

    const [hoveredSkillIndex, setHoveredSkillIndex] = useState<number | null>(null);
    const [barsFilled, setBarsFilled] = useState(false);

    // 3D Tilt for Verified Seal
    const heroRef = useRef<HTMLElement>(null);
    const sealRef = useRef<HTMLDivElement>(null);

    const handlePointerMove = (e: React.PointerEvent<HTMLElement>) => {
        if (!heroRef.current || !sealRef.current) return;
        const heroRect = heroRef.current.getBoundingClientRect();
        const sealRect = sealRef.current.getBoundingClientRect();
        const dx = (e.clientX - (sealRect.left + sealRect.width / 2)) / heroRect.width;
        const dy = (e.clientY - (sealRect.top + sealRect.height / 2)) / heroRect.height;
        sealRef.current.style.setProperty('--ty', `${(dx * 28).toFixed(2)}deg`);
        sealRef.current.style.setProperty('--tx', `${(-dy * 28).toFixed(2)}deg`);
    };

    const handlePointerLeave = () => {
        if (!sealRef.current) return;
        sealRef.current.style.setProperty('--tx', '0deg');
        sealRef.current.style.setProperty('--ty', '0deg');
    };

    const fetchOrGenerateLinkedInPost = async () => {
        if (linkedInPostContent) return;
        try {
            setLoadingLinkedInPost(true);
            const res = await studentService.generateLinkedInPost();
            if (res.data?.data?.linkedin_post_content) {
                setLinkedInPostContent(res.data.data.linkedin_post_content);
            }
        } catch (err) {
            console.error('Failed to load/generate LinkedIn post:', err);
        } finally {
            setLoadingLinkedInPost(false);
        }
    };

    const openShareModal = (platform: SharePlatform = 'linkedin') => {
        setActiveShareTab(platform);
        setShowShareModal(true);
        if (platform === 'linkedin') {
            fetchOrGenerateLinkedInPost();
        }
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await studentService.getSkillProfile();
                setData(res.data.data);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to load skill profile and credential details.');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    // Esc key to close modal & scroll lock
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (modalOpen) setModalOpen(false);
                if (showShareModal) setShowShareModal(false);
            }
        };
        if (modalOpen || showShareModal) {
            window.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                document.body.style.overflow = '';
            };
        }
    }, [modalOpen, showShareModal]);

    const cleanCohortName = (name?: string | null): string => {
        if (!name) return 'August 2026';
        let cleaned = name.trim();
        cleaned = cleaned.replace(/^(.+?)\s*\(\1\)$/i, '$1').trim();
        cleaned = cleaned.replace(/\(([^)]+)\)\s*\(\1\)/gi, '($1)').trim();
        const match = cleaned.match(/^(.+?)\s*\((.+?)\)$/i);
        if (match && match[1].trim().toLowerCase() === match[2].trim().toLowerCase()) {
            cleaned = match[1].trim();
        }
        return cleaned || 'August 2026';
    };

    const cleanProjectTitle = (title?: string | null): string => {
        if (!title) return 'Capstone Project Overview';
        let cleaned = title.trim();
        cleaned = cleaned.replace(/\(\s+/g, '(').replace(/\[\s+/g, '[');
        cleaned = cleaned.replace(/\s+\)/g, ')').replace(/\s+\]/g, ']');
        cleaned = cleaned.replace(/\s+([,.:;!?])/g, '$1');
        cleaned = cleaned.replace(/\s{2,}/g, ' ');
        return cleaned.trim() || 'Capstone Project Overview';
    };

    const formattedDate = (dateStr?: string | null) => {
        if (!dateStr) return '15 September 2026';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    const openInNewTab = (path?: string | null) => {
        if (!path) return;
        const url = getFileUrl(path);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const getInitials = (name?: string) => {
        if (!name) return 'FP';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        return name.slice(0, 2).toUpperCase();
    };

    const isLocalhost = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' || 
        window.location.hostname === '127.0.0.1' || 
        window.location.hostname === '::1'
    );

    const publicVerifyUrl = data ? `${window.location.origin}/verify/${data.id || data.enrollment_number}` : '';
    const liveVerifyUrl = data ? `https://thepurposeacademy.ai/verify/${data.id || data.enrollment_number}` : '';

    const handleCopyLink = () => {
        if (!publicVerifyUrl) return;
        navigator.clipboard.writeText(publicVerifyUrl);
        setCopied(true);
        toast.success('Public verification link copied to clipboard!');
        setTimeout(() => setCopied(false), 2500);
    };

    const getSuggestedCaption = () => {
        if (linkedInPostContent) return linkedInPostContent;
        if (!data) return '';
        const projectText = data.project?.project_title ? ` During the program, I developed my capstone project: "${cleanProjectTitle(data.project.project_title)}".` : '';
        const linkInCaption = isLocalhost ? liveVerifyUrl : publicVerifyUrl;
        return `🎓 Proud to share that I completed the AI Fellowship Program and presented my project on 12/9/2026 for this cohort!${projectText}\n\nCheck out my official verified credential and project showcase here:\n${linkInCaption}\n\nThis incredible journey wouldn't have been possible without the guidance and support of amazing leaders, Manav Subodh and Parth Singh. I want to express my deepest gratitude to The Purpose Academy (TPA) for providing such a transformative fellowship experience that empowers students to solve real-world problems.\n\n@Parth Singh @Manav Subodh\n\n#ThePurposeAcademy #TPA #AIFellowship #ArtificialIntelligence #Innovation #StudentInnovators`;
    };

    const handleCopySuggestedCaption = () => {
        const caption = getSuggestedCaption();
        navigator.clipboard.writeText(caption);
        setCaptionCopied(true);
        toast.success('LinkedIn post caption copied to clipboard!');
        setTimeout(() => setCaptionCopied(false), 2500);
    };

    const handleShareLinkedInPost = () => {
        if (!data) return;
        const caption = getSuggestedCaption();
        navigator.clipboard.writeText(caption);
        toast.success('Caption & mentions copied! Paste (Ctrl+V) into your LinkedIn post.', { duration: 4000 });

        const shareUrl = 'https://www.linkedin.com/feed/?shareActive=true';
        window.open(shareUrl, '_blank', 'noopener,noreferrer');
    };

    const handleDownloadPresentationScreenshot = async () => {
        if (!data?.project?.project_screenshot_path) return;
        const sanitizedName = (data.student_name || 'Fellow').replace(/[^a-zA-Z0-9_-]/g, '_');
        const fileName = `${sanitizedName}_Demo_Screenshot.png`;
        try {
            setDownloadingScreenshot(true);
            const imgUrl = getFileUrl(data.project.project_screenshot_path);
            const response = await fetch(imgUrl);
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success('Demo presentation screenshot downloaded!');
        } catch {
            toast.error('Failed to download presentation screenshot');
        } finally {
            setDownloadingScreenshot(false);
        }
    };

    const getSuggestedTweet = () => {
        if (!data) return '';
        const linkToShare = isLocalhost ? liveVerifyUrl : publicVerifyUrl;
        const projectTitle = cleanProjectTitle(data.project?.project_title);
        const titleSnippet = projectTitle 
            ? ` Capstone: "${projectTitle.length > 50 ? projectTitle.slice(0, 47) + '...' : projectTitle}".` 
            : '';
        return `🎓 Proud to share that I have completed the AI Impact Fellowship at The Purpose Academy!${titleSnippet}\n\nVerify my credential:\n${linkToShare}\n\n#AI #Fellowship #ThePurposeAcademy #Innovation`;
    };

    const handleCopyTwitterText = () => {
        const text = getSuggestedTweet();
        navigator.clipboard.writeText(text);
        setTwitterCopied(true);
        toast.success('Tweet text copied to clipboard!');
        setTimeout(() => setTwitterCopied(false), 2500);
    };

    const handleShareTwitterPost = () => {
        if (!data) return;
        const tweetText = getSuggestedTweet();
        navigator.clipboard.writeText(tweetText);
        toast.success('Tweet text copied! Opening X (Twitter)...', { duration: 3500 });
        
        const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
        const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        
        if (isMobile) {
            window.open(tweetUrl, '_blank');
        } else {
            const width = 600;
            const height = 480;
            const left = Math.max(0, (window.screen.width - width) / 2);
            const top = Math.max(0, (window.screen.height - height) / 2);
            window.open(
                tweetUrl,
                'twitter-share',
                `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${width},height=${height},top=${top},left=${left}`
            );
        }
    };

    const getInstagramCaption = () => {
        if (!data) return '';
        const linkToShare = isLocalhost ? liveVerifyUrl : publicVerifyUrl;
        const projectSnippet = data.project?.project_title 
            ? ` During the program, I developed my capstone project: "${cleanProjectTitle(data.project.project_title)}".` 
            : '';
        return `🎓 Excited to share that I have completed the AI Impact Fellowship at The Purpose Academy!${projectSnippet}\n\nCredential ID: ${data.certificate_number}\nVerify my official credential & project showcase via the link in bio or Story sticker:\n🔗 ${linkToShare}\n\n#AI #ThePurposeAcademy #Fellowship #ArtificialIntelligence #StudentAchievement #Innovation`;
    };

    const handleCopyInstagramCaption = () => {
        const caption = getInstagramCaption();
        navigator.clipboard.writeText(caption);
        setInstagramCopied(true);
        toast.success('Instagram caption & link copied to clipboard!');
        setTimeout(() => setInstagramCopied(false), 2500);
    };

    const handleOpenInstagram = () => {
        const caption = getInstagramCaption();
        navigator.clipboard.writeText(caption);
        toast.success('Caption copied! Opening Instagram...', { duration: 3500 });
        window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    };

    const handleDownloadCertificateImage = async () => {
        if (!data) return;
        const sanitizedName = (data.student_name || 'Fellow').replace(/[^a-zA-Z0-9_-]/g, '_');
        const fileName = `${sanitizedName}_Certificate.png`;

        if (data.certificate_image_url) {
            try {
                const imgUrl = getFileUrl(data.certificate_image_url);
                const response = await fetch(imgUrl);
                const blob = await response.blob();
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
                toast.success('Certificate image downloaded!');
                return;
            } catch {
                // fallback to PDF rendering
            }
        }

        if (!data.certificate_url) {
            toast.error('Certificate not available for download.');
            return;
        }

        try {
            setDownloadingImage(true);
            toast.loading('Rendering high-resolution certificate image...', { id: 'cert-dl' });
            const pdfUrl = getFileUrl(data.certificate_url);
            const loadingTask = pdfjsLib.getDocument({
                url: pdfUrl,
                withCredentials: false,
            });
            const pdf = await loadingTask.promise;
            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 3.0 });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (!context) throw new Error('Canvas context not available');

            canvas.height = viewport.height;
            canvas.width = viewport.width;

            await (page.render({
                canvasContext: context,
                viewport: viewport,
            } as any) as any).promise;

            canvas.toBlob((blob) => {
                if (blob) {
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                    toast.success('Certificate image downloaded! Ready to attach to your post or Story.', { id: 'cert-dl', duration: 4500 });
                } else {
                    toast.error('Failed to create image blob', { id: 'cert-dl' });
                }
            }, 'image/png');
        } catch (err) {
            console.error('Download error:', err);
            toast.dismiss('cert-dl');
            const link = document.createElement('a');
            link.href = getFileUrl(data.certificate_url);
            link.download = `${sanitizedName}_Certificate.pdf`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Certificate file downloaded!');
        } finally {
            setDownloadingImage(false);
        }
    };

    const handleAddToLinkedInProfile = () => {
        if (!data) return;
        const certName = 'AI Impact Fellowship - Certificate of Completion';
        const orgName = 'The Purpose Academy (TPA)';
        const date = data.completion_date || data.issued_at ? new Date(data.completion_date || data.issued_at!) : new Date();
        const issueYear = date.getFullYear();
        const issueMonth = date.getMonth() + 1;
        const certId = data.certificate_number || '';
        const certVerificationUrl = isLocalhost ? liveVerifyUrl : publicVerifyUrl;

        const params = new URLSearchParams({
            startTask: 'CERTIFICATION_NAME',
            name: certName,
            organizationName: orgName,
            issueYear: issueYear.toString(),
            issueMonth: issueMonth.toString(),
            certUrl: certVerificationUrl,
            certId: certId,
        });

        window.open(`https://www.linkedin.com/profile/add?${params.toString()}`, '_blank', 'noopener,noreferrer');
    };

    // Dynamic Skill calculation from data.skill_gains
    // Converts 1.0 - 5.0 scale to 0 - 100 percentage
    const skills: SkillItem[] = useMemo(() => {
        const gains = data?.skill_gains;
        const toPercent = (val: number | undefined | null): number => {
            if (val == null || isNaN(val) || val <= 0) return 0;
            const pct = val > 5 ? Math.min(100, val) : Math.min(100, Math.max(0, (val / 5) * 100));
            return Math.round((pct + Number.EPSILON) * 10) / 10;
        };

        return [
            {
                key: 'presentation',
                label: 'Presentation',
                score: toPercent(gains?.presentation),
                color: '#B600DE',
            },
            {
                key: 'problem_identification',
                label: 'Problem identification',
                score: toPercent(gains?.problem_identification),
                color: '#0090D8',
            },
            {
                key: 'problem_solving',
                label: 'Problem solving',
                score: toPercent(gains?.problem_solving),
                color: '#00B26F',
            },
            {
                key: 'coding_skill',
                label: 'Coding skill',
                score: toPercent(gains?.coding_skill),
                color: '#FF8000',
            },
            {
                key: 'journey_score',
                label: 'Growth and learning',
                score: toPercent(gains?.journey_score),
                color: '#F11B1B',
            },
        ];
    }, [data?.skill_gains]);

    // Calculate overall average score out of 100
    const overallScore = useMemo(() => {
        if (!skills || skills.length === 0) return 0;
        const nonZero = skills.filter((s) => s.score > 0);
        if (nonZero.length === 0) return 0;
        const avg = nonZero.reduce((acc, s) => acc + s.score, 0) / nonZero.length;
        return Math.round((avg + Number.EPSILON) * 10) / 10;
    }, [skills]);

    // Animated count-up score
    const [displayScore, setDisplayScore] = useState(0);
    useEffect(() => {
        if (overallScore <= 0) {
            setDisplayScore(0);
            return;
        }
        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (prefersReducedMotion) {
            setDisplayScore(overallScore);
            return;
        }

        let startTime: number | null = null;
        const duration = 1400;
        let rafId: number;

        const tick = (timestamp: number) => {
            if (startTime === null) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const easeOutCubic = 1 - Math.pow(1 - progress, 3);
            if (progress >= 1) {
                setDisplayScore(overallScore);
            } else {
                const current = overallScore * easeOutCubic;
                setDisplayScore(Math.round((current + Number.EPSILON) * 10) / 10);
            }
            if (progress < 1) {
                rafId = requestAnimationFrame(tick);
            }
        };

        const timer = setTimeout(() => {
            rafId = requestAnimationFrame(tick);
        }, 400);

        return () => {
            clearTimeout(timer);
            cancelAnimationFrame(rafId);
        };
    }, [overallScore]);

    useEffect(() => {
        const t = setTimeout(() => setBarsFilled(true), 400);
        return () => clearTimeout(t);
    }, []);

    // Close certificate modal on Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && modalOpen) {
                setModalOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [modalOpen]);

    // Geometry for Radar Chart
    const R = 118;
    const n = skills.length;
    const pt = (i: number, r: number): [number, number] => {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        return [Math.cos(a) * r, Math.sin(a) * r];
    };
    const poly = (f: number): string =>
        skills.map((_, i) => pt(i, R * f).map((v) => v.toFixed(2)).join(',')).join(' ');

    const shapePoints = skills
        .map((s, i) => pt(i, (R * Math.max(s.score, 5)) / 100).map((v) => v.toFixed(2)).join(','))
        .join(' ');

    if (loading) {
        return (
            <div className="tpa-profile student-view" data-theme={theme} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 16 }}>
                <div style={{ width: 44, height: 44, border: '4px solid var(--line)', borderTopColor: 'var(--text)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <p style={{ fontSize: 14, color: 'var(--text-3)' }}>Loading your verified Skill Profile...</p>
            </div>
        );
    }

    if (data?.is_locked) {
        const completion = data.profile_completion || {
            percentage: 0,
            is_complete: false,
            sections: {
                basic_details: false,
                school_contact: false,
                academic_profile: false,
                profile_picture: false,
                project_upload: false,
            },
        };

        const checklist = [
            { label: 'Basic Personal Details', completed: completion.sections?.basic_details },
            { label: 'School & Contact Information', completed: completion.sections?.school_contact },
            { label: 'Academic & Technical Profile', completed: completion.sections?.academic_profile },
            { label: 'Profile Picture / Avatar', completed: completion.sections?.profile_picture },
            { label: 'Capstone Demo Presentation (PDF)', completed: completion.sections?.project_upload },
        ];

        return (
            <div className="tpa-profile student-view" data-theme={theme} style={{ maxWidth: 680, margin: '40px auto', padding: '0 20px' }}>
                <div className="panel" style={{ textAlign: 'center', padding: '48px 32px', borderRadius: 24, boxShadow: '0 10px 30px rgba(0,0,0,0.06)' }}>
                    <div style={{ width: 72, height: 72, borderRadius: 24, background: 'rgba(245, 158, 11, 0.12)', color: '#D97706', display: 'grid', placeItems: 'center', margin: '0 auto 24px', border: '1px solid rgba(245, 158, 11, 0.25)' }}>
                        <Lock size={36} />
                    </div>

                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 14px', borderRadius: 999, background: 'rgba(245, 158, 11, 0.15)', color: '#B45309', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
                        <Lock size={12} />
                        Profile Incomplete ({completion.percentage}%)
                    </div>

                    <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
                        Skill Profile Locked
                    </h2>
                    <p style={{ fontSize: 15, color: 'var(--text-2)', maxWidth: 480, margin: '0 auto 28px', lineHeight: 1.6, fontWeight: 500 }}>
                        {data.message || 'Please complete your profile section to 100% after that unlock'}
                    </p>

                    {/* Progress Bar */}
                    <div style={{ maxWidth: 460, margin: '0 auto 32px', textAlign: 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                            <span>Completion Progress</span>
                            <span style={{ color: '#D97706', fontWeight: 700 }}>{completion.percentage}%</span>
                        </div>
                        <div style={{ width: '100%', height: 10, borderRadius: 999, background: 'var(--line)', overflow: 'hidden' }}>
                            <div style={{ width: `${completion.percentage}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, #F59E0B, #10B981)', transition: 'width 0.4s ease' }} />
                        </div>

                        {/* Section Checklist */}
                        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {checklist.map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 12, background: 'rgba(0,0,0,0.02)', border: '1px solid var(--line)' }}>
                                    <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{item.label}</span>
                                    {item.completed ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#10B981', fontSize: 12, fontWeight: 600 }}>
                                            <CheckCircle2 size={16} /> Verified
                                        </span>
                                    ) : (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#F59E0B', fontSize: 12, fontWeight: 600 }}>
                                            <Lock size={14} /> Incomplete
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={() => navigate('/student/profile')}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            padding: '12px 28px',
                            background: 'var(--text)',
                            color: 'var(--panel)',
                            borderRadius: 14,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            border: 'none',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <span>Complete Profile Section</span>
                        <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="tpa-profile student-view" data-theme={theme} style={{ maxWidth: 640, margin: '24px auto', padding: '0 16px' }}>
                <div className="panel" style={{ textAlign: 'center', padding: '40px 32px' }}>
                    <div style={{ width: 64, height: 64, borderRadius: 20, background: 'rgba(241, 27, 27, 0.08)', color: 'var(--tpa-red)', display: 'grid', placeItems: 'center', margin: '0 auto 20px', border: '1px solid var(--line)' }}>
                        <AlertCircle size={32} />
                    </div>
                    <h2 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                        Skill Profile Unavailable
                    </h2>
                    <p style={{ fontSize: 14, color: 'var(--text-2)', maxWidth: 440, margin: '0 auto' }}>
                        {error || 'The Skill Profile module is not enabled for your cohort.'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="tpa-profile student-view" data-theme={theme}>
                <main className="wrap" style={{ padding: '8px 20px 24px' }}>
                    {/* ================= HERO ================= */}
                    <section 
                        className="hero" 
                        ref={heroRef}
                        onPointerMove={handlePointerMove}
                        onPointerLeave={handlePointerLeave}
                        aria-label="Fellow credential"
                    >
                        <div className="orbs" aria-hidden="true">
                            <i /><i /><i />
                        </div>
                        <div className="hero-inner">
                            <div>
                                <div className="hello">
                                    {data.profile_picture ? (
                                        <img 
                                            src={getFileUrl(data.profile_picture)} 
                                            alt={data.student_name} 
                                            className="avatar"
                                        />
                                    ) : (
                                        <div className="avatar" aria-hidden="true">
                                            {getInitials(data.student_name)}
                                        </div>
                                    )}
                                    <p>
                                        <b>Skills profile</b>
                                        <span>AI Impact Fellowship, {cleanCohortName(data.cohort_name)} cohort</span>
                                    </p>
                                </div>

                                {/* Fellow Name with animated masked letters */}
                                <h1 id="fellowName" aria-label={data.student_name}>
                                    {[...data.student_name].map((char, idx) => (
                                        <span key={idx} className="ch" aria-hidden="true">
                                            <span style={{ animationDelay: `${0.95 + idx * 0.06}s` }}>
                                                {char === ' ' ? '\u00A0' : char}
                                            </span>
                                        </span>
                                    ))}
                                </h1>

                                {/* Name strokes underline */}
                                <div className="name-strokes" aria-hidden="true">
                                    <i style={{ flex: 3, background: 'var(--tpa-green)' }} />
                                    <i style={{ flex: 1.2, background: 'var(--tpa-red)' }} />
                                    <i style={{ flex: 2, background: 'var(--tpa-orange)' }} />
                                    <i style={{ flex: 2.6, background: 'var(--tpa-purple)' }} />
                                    <i style={{ flex: 2.2, background: 'var(--tpa-blue)' }} />
                                </div>

                                <p className="lede">
                                    Completed the <b>AI Impact Fellowship</b>, a four-week mentor-guided program, and presented a capstone solution at Demo Day.
                                </p>

                                <div className="hero-actions">
                                    {data.certificate_url && (
                                        <a 
                                            className="btn btn-primary" 
                                            href={getFileUrl(data.certificate_url)} 
                                            target="_blank" 
                                            rel="noreferrer" 
                                            download
                                        >
                                            <Download size={16} />
                                            Download certificate
                                        </a>
                                    )}
                                    <button 
                                        className="btn btn-ghost" 
                                        onClick={() => openShareModal('linkedin')}
                                        title="Share credential on LinkedIn, Twitter, or Instagram"
                                    >
                                        <Share2 size={16} />
                                        Share Credential
                                    </button>
                                    <button 
                                        className="btn btn-ghost" 
                                        onClick={handleAddToLinkedInProfile}
                                        title="Add certificate to LinkedIn profile"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                            <path d="M4.98 3.5a2.5 2.5 0 11-.01 5 2.5 2.5 0 01.01-5zM3 9.5h4V21H3zM9.5 9.5h3.8v1.6h.1c.5-1 1.8-2 3.8-2 4 0 4.8 2.6 4.8 6V21h-4v-5.2c0-1.2 0-2.8-1.7-2.8s-2 1.3-2 2.7V21h-4z"/>
                                        </svg>
                                        Add to LinkedIn
                                    </button>
                                    <button 
                                        className="btn btn-ghost" 
                                        onClick={handleCopyLink}
                                        title="Copy public verification link"
                                    >
                                        {copied ? <Check size={16} style={{ color: 'var(--ok)' }} /> : <Copy size={16} />}
                                        {copied ? 'Link Copied' : 'Copy profile link'}
                                    </button>
                                    {publicVerifyUrl && (
                                        <a 
                                            className="btn btn-ghost" 
                                            href={publicVerifyUrl} 
                                            target="_blank" 
                                            rel="noreferrer"
                                            title="View public verification page"
                                        >
                                            <ExternalLink size={16} />
                                            Public Page
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Verified Seal */}
                            <div className="seal-stage">
                                <div className="seal" ref={sealRef} aria-label="Overall fellowship score">
                                    <div className="ring blur" />
                                    <div className="ring" />
                                    <svg className="ticks" viewBox="-100 -100 200 200" aria-hidden="true" id="ticks">
                                        {Array.from({ length: 72 }, (_, i) => {
                                            const a = (i * 5 * Math.PI) / 180;
                                            const major = i % 18 === 0;
                                            const r1 = major ? 88 : 93;
                                            const r2 = 99;
                                            return (
                                                <line
                                                    key={i}
                                                    x1={Math.cos(a) * r1}
                                                    y1={Math.sin(a) * r1}
                                                    x2={Math.cos(a) * r2}
                                                    y2={Math.sin(a) * r2}
                                                    stroke={major ? 'var(--text)' : 'var(--text-3)'}
                                                    strokeWidth={major ? 2 : 1}
                                                    strokeLinecap="round"
                                                />
                                            );
                                        })}
                                    </svg>
                                    <div className="core">
                                        <span className="score" id="overall">{formatScore(displayScore)}</span>
                                        <span className="of">Overall, out of 100</span>
                                        <span className="lvl" id="overallLvl">
                                            <CheckCircle2 size={12} />
                                            <span>{getLevel(overallScore)}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Credential Facts Strip */}
                        <dl className="facts">
                            <div className="fact">
                                <dt><small>Program</small></dt>
                                <dd><b>AI Impact Fellowship</b></dd>
                            </div>
                            <div className="fact">
                                <dt><small>Cohort</small></dt>
                                <dd><b>{cleanCohortName(data.cohort_name)}</b></dd>
                            </div>
                            <div className="fact">
                                <dt><small>Completed on</small></dt>
                                <dd><b>{formattedDate(data.completion_date || data.issued_at)}</b></dd>
                            </div>
                            <div className="fact">
                                <dt><small>Certificate ID</small></dt>
                                <dd>
                                    <b>
                                        {data.certificate_number}
                                        <button 
                                            className="copy" 
                                            onClick={() => {
                                                navigator.clipboard.writeText(data.certificate_number);
                                                toast.success('Certificate ID copied!');
                                            }}
                                            aria-label="Copy certificate ID"
                                            title="Copy certificate ID"
                                        >
                                            <Copy size={12} />
                                        </button>
                                    </b>
                                </dd>
                            </div>
                        </dl>
                    </section>

                    {/* ================= SKILLS PANEL ================= */}
                    <section className="panel skills-panel reveal in" aria-labelledby="skillHead">
                        <div className="panel-head">
                            <div>
                                <h2 id="skillHead">Skill profile</h2>
                                <p>Five competencies scored by mentors during the fellowship</p>
                            </div>
                        </div>

                        <div className="skills">
                            {/* Radar Chart SVG */}
                            <svg 
                                className="radar" 
                                id="radar" 
                                viewBox="-215 -170 430 345" 
                                role="img" 
                                aria-label="Competency Radar Chart"
                            >
                                {/* Radial concentric polygon rings */}
                                {[0.25, 0.5, 0.75, 1].map((f) => (
                                    <polygon key={f} className="ring" points={poly(f)} />
                                ))}

                                {/* Axis dashed lines */}
                                {skills.map((_, i) => {
                                    const [x, y] = pt(i, R);
                                    return <line key={i} className="axis" x1={0} y1={0} x2={x} y2={y} />;
                                })}

                                {/* Filled competency polygon */}
                                <polygon className="shape" points={shapePoints} />

                                {/* Spokes, glow, node points & text labels */}
                                {skills.map((s, i) => {
                                    const [x, y] = pt(i, (R * Math.max(s.score, 5)) / 100);
                                    const len = Math.hypot(x, y);
                                    const isDimmed = hoveredSkillIndex !== null && hoveredSkillIndex !== i;
                                    const [lx, ly] = pt(i, R + 24);
                                    const anchor = Math.abs(lx) < 8 ? 'middle' : lx > 0 ? 'start' : 'end';
                                    const dy = ly < -R ? -14 : 0;
                                    const sp1 = s.label.indexOf(' ');
                                    const lines = s.label.length > 13 && sp1 > 0 ? [s.label.slice(0, sp1), s.label.slice(sp1 + 1)] : [s.label];
                                    const top = ly + dy - (lines.length - 1) * 7;

                                    return (
                                        <g key={s.key}>
                                            <line 
                                                className="spoke-glow" 
                                                x1={0} 
                                                y1={0} 
                                                x2={x} 
                                                y2={y} 
                                                stroke={s.color} 
                                                style={{ opacity: isDimmed ? 0.1 : undefined }}
                                            />
                                            <line 
                                                className="spoke" 
                                                x1={0} 
                                                y1={0} 
                                                x2={x} 
                                                y2={y} 
                                                stroke={s.color} 
                                                strokeDasharray={len} 
                                                style={{ 
                                                    ['--len' as any]: len, 
                                                    animationDelay: `${0.3 + i * 0.08}s`,
                                                    opacity: isDimmed ? 0.15 : 1
                                                }} 
                                            />
                                            <circle 
                                                className="node" 
                                                cx={x} 
                                                cy={y} 
                                                r={7} 
                                                stroke={s.color} 
                                                style={{ animationDelay: `${0.9 + i * 0.08}s` }} 
                                            />

                                            <text x={lx} y={top} textAnchor={anchor} dominantBaseline="middle">
                                                {lines.map((ln, k) => (
                                                    <tspan key={k} x={lx} dy={k ? 14 : 0}>
                                                        {ln}
                                                    </tspan>
                                                ))}
                                            </text>
                                            <text 
                                                className="val" 
                                                x={lx} 
                                                y={top + (lines.length - 1) * 14 + 16} 
                                                textAnchor={anchor} 
                                                dominantBaseline="middle"
                                            >
                                                {formatScore(s.score)}
                                            </text>
                                        </g>
                                    );
                                })}

                                {/* Center Hub */}
                                <circle className="hub" cx={0} cy={0} r={9} />
                            </svg>

                            {/* Skill List */}
                            <ul className="skill-list" id="skillList">
                                {skills.map((s, i) => (
                                    <li 
                                        key={s.key} 
                                        className={`skill ${hoveredSkillIndex === i ? 'is-active' : ''}`}
                                        tabIndex={0}
                                        onMouseEnter={() => setHoveredSkillIndex(i)}
                                        onMouseLeave={() => setHoveredSkillIndex(null)}
                                        onFocus={() => setHoveredSkillIndex(i)}
                                        onBlur={() => setHoveredSkillIndex(null)}
                                    >
                                        <div className="skill-top">
                                            <span className="skill-name">
                                                <i style={{ background: s.color }} />
                                                {s.label}
                                            </span>
                                            <span className="skill-score">
                                                <small>{getLevel(s.score)}</small>
                                                {formatScore(s.score)}
                                            </span>
                                        </div>
                                        <span className="track">
                                            <span 
                                                className="fill" 
                                                style={{ 
                                                    background: s.color, 
                                                    color: s.color, 
                                                    width: barsFilled ? `${s.score}%` : '0%' 
                                                }} 
                                            />
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>

                    {/* ================= CERTIFICATE + CAPSTONE PROJECT ================= */}
                    <div className="grid">
                        {/* Certificate Panel */}
                        <section 
                            className="panel cert-panel reveal in" 
                            id="certificate" 
                            aria-labelledby="certHead"
                        >
                            <div className="panel-head">
                                <div>
                                    <h2 id="certHead">Certificate</h2>
                                    <p>The QR code on this certificate opens this page</p>
                                </div>
                            </div>

                            <div 
                                className="cert-frame" 
                                id="certOpen" 
                                role="button" 
                                tabIndex={0} 
                                aria-label="Open certificate preview"
                                onClick={() => setModalOpen(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setModalOpen(true);
                                    }
                                }}
                            >
                                <div className="cert-preview-wrapper">
                                    {!certImgFailed && data.certificate_image_url ? (
                                        <img
                                            src={getFileUrl(data.certificate_image_url)}
                                            alt="Certificate of Completion"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={() => {
                                                if (data.id && !data.certificate_image_url?.includes('/preview')) {
                                                    const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050/api/v1').replace(/\/+$/, '');
                                                    const previewUrl = `${apiBase}/admin/students/public/certificate/${data.id}/preview`;
                                                    setData(prev => prev ? { ...prev, certificate_image_url: previewUrl } : null);
                                                } else {
                                                    setCertImgFailed(true);
                                                }
                                            }}
                                        />
                                    ) : data.certificate_url ? (
                                        <PdfThumbnail
                                            url={getFileUrl(data.certificate_url)}
                                            alt="Certificate of Completion"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div style={{ display: 'grid', placeItems: 'center', height: '100%', color: 'var(--text-3)' }}>
                                            Certificate Image Preview
                                        </div>
                                    )}
                                </div>

                                {/* Enlarge Badge Button */}
                                <div className="cert-enlarge-badge" aria-hidden="true">
                                    <ZoomIn size={13} />
                                    <span>Click to enlarge</span>
                                </div>
                            </div>

                            <div className="cert-meta">
                                <div>
                                    <h3>Certificate of completion</h3>
                                    <p>AI Impact Fellowship</p>
                                </div>
                                <div className="icon-row">
                                    <button 
                                        className="icon-btn" 
                                        onClick={() => openInNewTab(data.certificate_url || data.certificate_image_url)}
                                        aria-label="Open certificate in new tab"
                                        title="Open in new tab"
                                    >
                                        <ExternalLink size={18} />
                                    </button>
                                    {data.certificate_url && (
                                        <a 
                                            className="icon-btn" 
                                            href={getFileUrl(data.certificate_url)} 
                                            target="_blank" 
                                            rel="noreferrer" 
                                            download
                                            aria-label="Download certificate"
                                            title="Download certificate"
                                        >
                                            <Download size={18} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* Capstone Project Panel */}
                        <section className="panel project reveal in" aria-labelledby="projHead">
                            <div>
                                <div className="panel-head" style={{ marginBottom: 14 }}>
                                    <div>
                                        <h2 id="projHead">Capstone project</h2>
                                        <p>Built and presented during the fellowship</p>
                                    </div>
                                </div>

                                <h3 className="title">
                                    {cleanProjectTitle(data.project?.project_title)}
                                </h3>
                                <p className="desc">
                                    {data.project?.project_description || `${data.student_name} completed the capstone curriculum and presented a working solution at the fellowship Demo Day.`}
                                </p>

                                <dl className="kv">
                                    {(data.school_name || data.project?.school_name) && (
                                        <div>
                                            <dt>School</dt>
                                            <dd>{data.school_name || data.project?.school_name}</dd>
                                        </div>
                                    )}
                                    <div>
                                        <dt>Problem area</dt>
                                        <dd>{(data.project as any)?.problem_area || 'Education'}</dd>
                                    </div>
                                    <div>
                                        <dt>Presented at</dt>
                                        <dd>Demo Day, 12/09/2026</dd>
                                    </div>
                                    <div>
                                        <dt>Tools used</dt>
                                        <dd className="tags">
                                            <span className="chip">Python</span>
                                            <span className="chip">Generative AI</span>
                                            <span className="chip">No-code</span>
                                        </dd>
                                    </div>
                                </dl>
                            </div>

                            {/* Demo Presentation / Deck Preview or Empty State */}
                            {data.project?.demo_presentation_path || data.project?.project_screenshot_path ? (
                                <div className="slide-stage">
                                    <div className="slide-toolbar">
                                        <button
                                            type="button"
                                            onClick={() => openInNewTab(data.project!.demo_presentation_path || data.project!.project_screenshot_path!)}
                                            className="slide-open-btn"
                                        >
                                            <span>Open in new tab</span>
                                            <ExternalLink size={14} />
                                        </button>
                                        <span 
                                            className="slide-filename"
                                            title={data.project.demo_presentation_name || data.project.project_screenshot_name || 'Capstone-Presentation.pdf'}
                                        >
                                            {data.project.demo_presentation_name || data.project.project_screenshot_name || 'Capstone-Presentation.pdf'}
                                        </span>
                                    </div>
                                    <div 
                                        onClick={() => openInNewTab(data.project!.demo_presentation_path || data.project!.project_screenshot_path!)}
                                        style={{ aspectRatio: '16/9', width: '100%', background: '#000', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
                                    >
                                        {data.project.project_screenshot_path ? (
                                            <img 
                                                src={getFileUrl(data.project.project_screenshot_path)} 
                                                alt="Project Presentation Preview" 
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <PdfThumbnail 
                                                url={getFileUrl(data.project.demo_presentation_path!)} 
                                                alt="Presentation Deck"
                                                className="w-full h-full object-contain"
                                                scale={2}
                                            />
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="empty">
                                    <div className="ico">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="13" rx="2"/>
                                            <path d="M8 21h8M12 17v4M10 8.5v5l4-2.5z"/>
                                        </svg>
                                    </div>
                                    <h3>Demo presentation not added yet</h3>
                                    <p>You can upload slides or a screenshot from your profile page.</p>
                                </div>
                            )}
                        </section>
                    </div>
                </main>

                {/* ================= CERTIFICATE PREVIEW MODAL ================= */}
                <div 
                    className={`modal cert-modal-view ${modalOpen ? 'open' : ''}`}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setModalOpen(false);
                    }}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Certificate preview enlarged"
                >
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button 
                            type="button"
                            className="modal-close" 
                            onClick={() => setModalOpen(false)}
                            aria-label="Close modal"
                        >
                            <X size={20} />
                        </button>
                        {!certImgFailed && data?.certificate_image_url ? (
                            <img 
                                src={getFileUrl(data.certificate_image_url)} 
                                alt="Certificate of Completion Preview" 
                                onError={() => setCertImgFailed(true)}
                            />
                        ) : data?.certificate_url ? (
                            <div style={{ width: '100%', height: '80vh', background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
                                <iframe 
                                    src={getFileUrl(data.certificate_url)} 
                                    title="Certificate Preview"
                                    style={{ width: '100%', height: '100%', border: 'none' }}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Multi-Platform Sharing Modal */}
            {showShareModal && (
                <div 
                    className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200"
                    onClick={() => setShowShareModal(false)}
                >
                    <div 
                        className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-200 dark:border-slate-800 max-w-lg w-full max-h-[88vh] overflow-y-auto custom-scrollbar p-5 sm:p-6 space-y-4 relative animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Sticky Modal Top: Header + Platform Tabs */}
                        <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xs z-10 -mx-5 sm:-mx-6 px-5 sm:px-6 -mt-1 pt-1 pb-3 space-y-3.5 border-b border-gray-100 dark:border-slate-800">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                    <div className={`w-9 h-9 text-white rounded-xl flex items-center justify-center shadow-xs transition-colors ${
                                        activeShareTab === 'linkedin' 
                                            ? 'bg-[#0A66C2]' 
                                            : activeShareTab === 'twitter' 
                                                ? 'bg-black' 
                                                : 'bg-gradient-to-tr from-[#fd1d1d] via-[#e1306c] to-[#833ab4]'
                                    }`}>
                                        {activeShareTab === 'linkedin' && <LinkedinIcon size={18} />}
                                        {activeShareTab === 'twitter' && <TwitterIcon size={16} />}
                                        {activeShareTab === 'instagram' && <InstagramIcon size={18} />}
                                    </div>
                                    <div>
                                        <h3 className="text-base sm:text-lg font-bold text-[#0B1533] dark:text-white">
                                            {activeShareTab === 'linkedin' && 'Share on LinkedIn'}
                                            {activeShareTab === 'twitter' && 'Share on Twitter / X'}
                                            {activeShareTab === 'instagram' && 'Share on Instagram'}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-slate-400">
                                            {activeShareTab === 'linkedin' && 'Showcase your certificate to your professional network'}
                                            {activeShareTab === 'twitter' && 'Tweet your verified achievement with auto-unfurled card'}
                                            {activeShareTab === 'instagram' && 'Share your credential on your Instagram Story or Feed'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowShareModal(false)}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Platform Navigation Tabs */}
                            <div className="grid grid-cols-3 gap-1 p-1 bg-gray-100/80 dark:bg-slate-800/80 rounded-xl border border-gray-200 dark:border-slate-700">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setActiveShareTab('linkedin');
                                        fetchOrGenerateLinkedInPost();
                                    }}
                                    className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                        activeShareTab === 'linkedin'
                                            ? 'bg-[#0A66C2] text-white shadow-xs'
                                            : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-slate-700/60'
                                    }`}
                                >
                                    <LinkedinIcon size={13} className={activeShareTab === 'linkedin' ? 'text-white' : 'text-[#0A66C2]'} />
                                    <span>LinkedIn</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveShareTab('twitter')}
                                    className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                        activeShareTab === 'twitter'
                                            ? 'bg-black text-white shadow-xs'
                                            : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-slate-700/60'
                                    }`}
                                >
                                    <TwitterIcon size={12} className={activeShareTab === 'twitter' ? 'text-white' : 'text-gray-900 dark:text-white'} />
                                    <span>Twitter / X</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveShareTab('instagram')}
                                    className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                        activeShareTab === 'instagram'
                                            ? 'bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white shadow-xs'
                                            : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-slate-700/60'
                                    }`}
                                >
                                    <InstagramIcon size={13} className={activeShareTab === 'instagram' ? 'text-white' : 'text-[#E1306C]'} />
                                    <span>Instagram</span>
                                </button>
                            </div>
                        </div>

                        {/* Modal Tab Body */}
                        <div className="space-y-3.5 pt-1">
                            {/* TAB 1: LINKEDIN */}
                            {activeShareTab === 'linkedin' && (
                                <>
                                    {/* Option 1: Post to LinkedIn Feed */}
                                    <div className="p-4 sm:p-4.5 rounded-2xl border-2 border-blue-100 dark:border-blue-900/40 bg-blue-50/30 dark:bg-blue-950/20 hover:bg-blue-50/50 dark:hover:bg-blue-950/30 transition-colors space-y-3">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-xs font-bold text-[#0A66C2] dark:text-[#38bdf8] uppercase tracking-wider">
                                                    Option 1 &bull; Feed Post
                                                </span>
                                                <span className="px-2 py-0.5 bg-[#0A66C2]/10 dark:bg-[#0A66C2]/30 text-[#0A66C2] dark:text-[#38bdf8] rounded-full text-[10px] font-bold">
                                                    Personalized &bull; Direct Media
                                                </span>
                                            </div>
                                            <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                                                Share Achievement to LinkedIn
                                            </h4>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
                                                Share your AI Fellowship achievement on your LinkedIn feed. Download your certificate image and presentation screenshot to attach directly for maximum reach!
                                            </p>
                                        </div>

                                        {/* Post Content Box */}
                                        <div className="bg-white/95 dark:bg-slate-800/90 rounded-xl p-3.5 border border-blue-100 dark:border-blue-900/50 space-y-2 shadow-2xs">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-1.5 font-bold text-xs text-[#0A66C2] dark:text-[#38bdf8]">
                                                    <Sparkles size={14} className="text-amber-500 shrink-0" />
                                                    <span>Suggested Post Caption</span>
                                                </div>
                                                <button
                                                    onClick={handleCopySuggestedCaption}
                                                    type="button"
                                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A66C2] hover:text-[#004182] dark:text-sky-400 cursor-pointer"
                                                >
                                                    {captionCopied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                                                    <span>{captionCopied ? 'Copied' : 'Copy Text'}</span>
                                                </button>
                                            </div>

                                            {loadingLinkedInPost ? (
                                                <div className="py-4 flex flex-col items-center justify-center gap-2 text-xs text-gray-500">
                                                    <Loader2 size={18} className="animate-spin text-[#0A66C2]" />
                                                    <span>Preparing your personalized post caption...</span>
                                                </div>
                                            ) : (
                                                <div className="max-h-40 overflow-y-auto pr-1 text-xs text-gray-700 dark:text-slate-300 leading-relaxed border-t border-gray-100 dark:border-slate-700/60 pt-2 font-sans select-all [&>p]:mb-2 [&>p:last-child]:mb-0 [&>p>strong]:font-bold [&>p>strong]:text-gray-900 dark:[&>p>strong]:text-white">
                                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                        {getSuggestedCaption()}
                                                    </ReactMarkdown>
                                                </div>
                                            )}
                                        </div>

                                        {/* Pro-Tip Box */}
                                        <div className="bg-blue-50/50 dark:bg-slate-900/40 rounded-xl p-3 border border-blue-100 dark:border-blue-900/40 text-xs text-gray-700 dark:text-slate-300 space-y-1">
                                            <span className="font-bold text-[#0A66C2] dark:text-[#38bdf8]">Quick Posting Guide:</span>
                                            <ol className="space-y-0.5 text-[11px] text-gray-600 dark:text-slate-400 pl-4 list-decimal leading-relaxed">
                                                <li>Download your certificate image (and project screenshot if available).</li>
                                                <li>Click <b>Open LinkedIn</b> (your post caption copies to clipboard).</li>
                                                <li>In LinkedIn, paste (<kbd className="px-1 py-0.5 bg-gray-100 dark:bg-slate-700 rounded text-[10px] font-mono">Ctrl+V</kbd>) and attach your downloaded media!</li>
                                            </ol>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="pt-1 space-y-2">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                <button
                                                    onClick={handleDownloadCertificateImage}
                                                    disabled={downloadingImage}
                                                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-800 dark:text-slate-200 border-2 border-blue-200 dark:border-blue-800 hover:border-[#0A66C2] rounded-xl text-xs font-bold shadow-2xs transition-colors cursor-pointer disabled:opacity-50 min-h-[42px]"
                                                    title="Download certificate PNG"
                                                >
                                                    {downloadingImage ? (
                                                        <>
                                                            <Loader2 size={14} className="animate-spin text-[#0A66C2] shrink-0" />
                                                            <span>Rendering Image...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ImageIcon size={14} className="text-[#0A66C2] shrink-0" />
                                                            <span>Download Certificate PNG</span>
                                                            <Download size={12} className="text-gray-400 shrink-0" />
                                                        </>
                                                    )}
                                                </button>

                                                {data?.project?.project_screenshot_path ? (
                                                    <button
                                                        onClick={handleDownloadPresentationScreenshot}
                                                        disabled={downloadingScreenshot}
                                                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-800 dark:text-slate-200 border-2 border-purple-200 dark:border-purple-800 hover:border-purple-500 rounded-xl text-xs font-bold shadow-2xs transition-colors cursor-pointer disabled:opacity-50 min-h-[42px]"
                                                        title="Download project demo presentation screenshot"
                                                    >
                                                        {downloadingScreenshot ? (
                                                            <>
                                                                <Loader2 size={14} className="animate-spin text-purple-600 shrink-0" />
                                                                <span>Downloading...</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ImageIcon size={14} className="text-purple-600 shrink-0" />
                                                                <span>Download Project Screenshot</span>
                                                                <Download size={12} className="text-gray-400 shrink-0" />
                                                            </>
                                                        )}
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={handleCopySuggestedCaption}
                                                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-200 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold shadow-2xs transition-colors cursor-pointer min-h-[42px]"
                                                    >
                                                        {captionCopied ? <Check size={14} className="text-emerald-600 shrink-0" /> : <Copy size={14} className="text-gray-500 shrink-0" />}
                                                        <span>{captionCopied ? 'Caption Copied!' : 'Copy Caption'}</span>
                                                    </button>
                                                )}
                                            </div>

                                            <button
                                                onClick={handleShareLinkedInPost}
                                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-xl text-xs font-bold shadow-xs transition-colors cursor-pointer whitespace-nowrap min-h-[42px]"
                                                title="Open LinkedIn to create a post (copies caption automatically)"
                                            >
                                                <LinkedinIcon size={15} className="shrink-0" />
                                                <span>Open LinkedIn to Post</span>
                                                <ExternalLink size={13} className="shrink-0" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Option 2: Add to LinkedIn Profile */}
                                    <div className="p-4 sm:p-4.5 rounded-2xl border-2 border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/30 dark:bg-emerald-950/20 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30 transition-colors space-y-3">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                                                    Option 2 &bull; Profile Feature
                                                </span>
                                                <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950/70 text-emerald-800 dark:text-emerald-300 rounded-full text-[10px] font-bold">
                                                    Permanent Credential
                                                </span>
                                            </div>
                                            <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                                                Add to Licenses &amp; Certifications
                                            </h4>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
                                                Permanently attach this credential to your LinkedIn profile with prefilled issuing organization, issue date, and credential ID ({data.certificate_number}).
                                            </p>
                                        </div>

                                        <div className="pt-1">
                                            <button
                                                onClick={() => {
                                                    handleAddToLinkedInProfile();
                                                    setShowShareModal(false);
                                                }}
                                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0B1533] hover:bg-[#1E293B] dark:bg-primary dark:hover:bg-blue-600 text-white rounded-xl text-xs font-bold shadow-xs transition-colors cursor-pointer"
                                            >
                                                <Award size={15} className="text-[#E07B12]" />
                                                <span>Add to LinkedIn Profile</span>
                                                <ExternalLink size={13} />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* TAB 2: TWITTER / X */}
                            {activeShareTab === 'twitter' && (
                                <div className="p-4 sm:p-4.5 rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/40 space-y-3.5">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1">
                                                <TwitterIcon size={12} />
                                                <span>Tweet on X</span>
                                            </span>
                                            <span className="px-2 py-0.5 bg-gray-900 dark:bg-slate-700 text-white rounded-full text-[10px] font-bold">
                                                Card Preview Supported
                                            </span>
                                        </div>
                                        <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                                            Post Credential to Twitter / X
                                        </h4>
                                        <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
                                            Share your completion tweet with an automated rich card preview. You can also attach your high-res certificate image!
                                        </p>
                                    </div>

                                    {/* Tweet Preview Box */}
                                    <div className="bg-white dark:bg-slate-800/90 rounded-xl p-3.5 border border-gray-200 dark:border-slate-700 shadow-2xs space-y-2">
                                        <div className="flex items-center justify-between text-[11px] text-gray-400 dark:text-slate-400">
                                            <span className="font-semibold text-gray-500 dark:text-slate-300">Suggested Tweet Content</span>
                                            <span className="font-mono">{getSuggestedTweet().length} / 280 chars</span>
                                        </div>
                                        <p className="text-xs text-gray-700 dark:text-slate-200 whitespace-pre-line leading-relaxed font-normal bg-gray-50/70 dark:bg-slate-900/70 p-2.5 rounded-lg border border-gray-100 dark:border-slate-800">
                                            {getSuggestedTweet()}
                                        </p>
                                        <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                                            <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 rounded text-[10px] font-semibold">#AI</span>
                                            <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 rounded text-[10px] font-semibold">#Fellowship</span>
                                            <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 rounded text-[10px] font-semibold">#ThePurposeAcademy</span>
                                            <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 rounded text-[10px] font-semibold">#Innovation</span>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="pt-1 space-y-2">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                            <button
                                                onClick={handleDownloadCertificateImage}
                                                disabled={downloadingImage}
                                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-800 dark:text-slate-200 border-2 border-gray-300 dark:border-slate-600 hover:border-black dark:hover:border-white rounded-xl text-xs font-bold shadow-2xs transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap min-h-[42px]"
                                                title="Download certificate PNG"
                                            >
                                                {downloadingImage ? (
                                                    <>
                                                        <Loader2 size={15} className="animate-spin text-gray-700 dark:text-slate-300 shrink-0" />
                                                        <span>Rendering Image...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <ImageIcon size={15} className="text-gray-700 dark:text-slate-300 shrink-0" />
                                                        <span>Download Image (PNG)</span>
                                                        <Download size={13} className="text-gray-400 dark:text-slate-500 shrink-0" />
                                                    </>
                                                )}
                                            </button>

                                            <button
                                                onClick={handleShareTwitterPost}
                                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-black dark:bg-slate-950 hover:bg-gray-800 dark:hover:bg-slate-900 border dark:border-slate-700 text-white rounded-xl text-xs font-bold shadow-xs transition-colors cursor-pointer whitespace-nowrap min-h-[42px]"
                                                title="Open Twitter / X composer (copies text automatically)"
                                            >
                                                <TwitterIcon size={14} className="shrink-0" />
                                                <span>Post on X (Twitter)</span>
                                                <ExternalLink size={13} className="shrink-0" />
                                            </button>
                                        </div>

                                        <button
                                            onClick={handleCopyTwitterText}
                                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-200 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 rounded-xl text-xs font-semibold shadow-2xs transition-colors cursor-pointer min-h-[38px]"
                                            title="Copy tweet text to clipboard"
                                        >
                                            {twitterCopied ? <Check size={14} className="text-emerald-600 dark:text-emerald-400 shrink-0" /> : <Copy size={14} className="text-gray-500 dark:text-slate-400 shrink-0" />}
                                            <span>{twitterCopied ? 'Tweet Text Copied to Clipboard!' : 'Copy Tweet Text'}</span>
                                        </button>
                                    </div>

                                    {/* Step Guide Box */}
                                    <div className="bg-white/95 dark:bg-slate-800/90 rounded-xl p-3 border border-gray-200 dark:border-slate-700 text-xs text-gray-700 dark:text-slate-300 space-y-1.5 shadow-2xs">
                                        <div className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-white">
                                            <Sparkles size={14} className="text-amber-500 shrink-0" />
                                            <span>Quick Posting Steps:</span>
                                        </div>
                                        <ol className="space-y-1 text-xs text-gray-600 dark:text-slate-300 pl-4 list-decimal leading-relaxed">
                                            <li>Click <b>Download Image</b> to save your crisp certificate PNG.</li>
                                            <li>Click <b>Post on X (Twitter)</b> to open the tweet composer with pre-filled text and link.</li>
                                            <li>X will automatically unfurl your verified card, and you can attach your image!</li>
                                        </ol>
                                    </div>
                                </div>
                            )}

                            {/* TAB 3: INSTAGRAM */}
                            {activeShareTab === 'instagram' && (
                                <div className="p-4 sm:p-4.5 rounded-2xl border-2 border-pink-100 dark:border-pink-900/40 bg-pink-50/20 dark:bg-pink-950/15 space-y-3.5">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-xs font-bold text-[#E1306C] dark:text-pink-400 uppercase tracking-wider flex items-center gap-1">
                                                <InstagramIcon size={13} />
                                                <span>Instagram Story &amp; Feed</span>
                                            </span>
                                            <span className="px-2 py-0.5 bg-gradient-to-r from-[#833ab4] to-[#fd1d1d] text-white rounded-full text-[10px] font-bold">
                                                3-Step Sharing Toolkit
                                            </span>
                                        </div>
                                        <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                                            Share on Instagram Story &amp; Post
                                        </h4>
                                        <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
                                            Instagram is a visual mobile app. Download your HD certificate image, copy your formatted caption, and share to your Story or Feed!
                                        </p>
                                    </div>

                                    {/* 3-Step Guided Toolkit */}
                                    <div className="space-y-2.5">
                                        {/* Step 1 */}
                                        <div className="bg-white dark:bg-slate-800/90 rounded-xl p-3 border border-pink-100 dark:border-pink-950/40 shadow-2xs flex items-center justify-between gap-3">
                                            <div className="space-y-0.5 min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-5 h-5 bg-pink-100 dark:bg-pink-950/60 text-[#E1306C] dark:text-pink-400 rounded-full text-xs font-bold flex items-center justify-center shrink-0">1</span>
                                                    <span className="text-xs font-bold text-gray-800 dark:text-slate-200">Download Certificate Image</span>
                                                </div>
                                                <p className="text-[11px] text-gray-500 dark:text-slate-400 pl-6.5">Save crystal-clear PNG to attach to your Instagram post or Story</p>
                                            </div>
                                            <button
                                                onClick={handleDownloadCertificateImage}
                                                disabled={downloadingImage}
                                                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-[#E1306C] dark:text-pink-400 border border-pink-200 dark:border-pink-800/60 hover:border-pink-400 rounded-lg text-xs font-bold shadow-2xs transition-colors cursor-pointer disabled:opacity-50"
                                            >
                                                {downloadingImage ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                                                <span>Save PNG</span>
                                            </button>
                                        </div>

                                        {/* Step 2 */}
                                        <div className="bg-white dark:bg-slate-800/90 rounded-xl p-3 border border-pink-100 dark:border-pink-950/40 shadow-2xs flex items-center justify-between gap-3">
                                            <div className="space-y-0.5 min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-5 h-5 bg-pink-100 dark:bg-pink-950/60 text-[#E1306C] dark:text-pink-400 rounded-full text-xs font-bold flex items-center justify-center shrink-0">2</span>
                                                    <span className="text-xs font-bold text-gray-800 dark:text-slate-200">Copy Caption &amp; Link</span>
                                                </div>
                                                <p className="text-[11px] text-gray-500 dark:text-slate-400 pl-6.5">Includes credential verification URL for Story &ldquo;Link Sticker&rdquo; or bio</p>
                                            </div>
                                            <button
                                                onClick={handleCopyInstagramCaption}
                                                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-800 dark:text-slate-200 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 rounded-lg text-xs font-bold shadow-2xs transition-colors cursor-pointer"
                                            >
                                                {instagramCopied ? <Check size={13} className="text-emerald-600 dark:text-emerald-400" /> : <Copy size={13} className="text-gray-500 dark:text-slate-400" />}
                                                <span>{instagramCopied ? 'Copied' : 'Copy'}</span>
                                            </button>
                                        </div>

                                        {/* Step 3 */}
                                        <div className="bg-white dark:bg-slate-800/90 rounded-xl p-3 border border-pink-100 dark:border-pink-950/40 shadow-2xs flex items-center justify-between gap-3">
                                            <div className="space-y-0.5 min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-5 h-5 bg-pink-100 dark:bg-pink-950/60 text-[#E1306C] dark:text-pink-400 rounded-full text-xs font-bold flex items-center justify-center shrink-0">3</span>
                                                    <span className="text-xs font-bold text-gray-800 dark:text-slate-200">Open Instagram</span>
                                                </div>
                                                <p className="text-[11px] text-gray-500 dark:text-slate-400 pl-6.5">Opens app on mobile, or web on desktop</p>
                                            </div>
                                            <button
                                                onClick={handleOpenInstagram}
                                                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] hover:opacity-90 text-white rounded-lg text-xs font-bold shadow-xs transition-opacity cursor-pointer"
                                            >
                                                <InstagramIcon size={13} />
                                                <span>Open App</span>
                                                <ExternalLink size={12} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Story Pro-Tip Callout */}
                                    <div className="bg-white/95 dark:bg-slate-800/90 rounded-xl p-3 border border-amber-200 dark:border-amber-900/40 text-xs text-amber-900 dark:text-amber-200 space-y-1 shadow-2xs">
                                        <div className="flex items-center gap-1.5 font-bold text-amber-700 dark:text-amber-400">
                                            <Sparkles size={14} className="text-amber-500 shrink-0" />
                                            <span>Story Sticker Pro-Tip:</span>
                                        </div>
                                        <p className="text-xs text-gray-600 dark:text-slate-300 pl-5 leading-relaxed">
                                            When posting on your <b>Instagram Story</b>, tap the Sticker icon, choose <b>&ldquo;LINK&rdquo;</b>, and paste your verification URL so viewers can verify your credential with one tap!
                                        </p>
                                    </div>

                                    {/* Caption Preview Box */}
                                    <div className="bg-white dark:bg-slate-800/90 rounded-xl p-3 border border-gray-200 dark:border-slate-700 shadow-2xs space-y-1.5">
                                        <div className="flex items-center justify-between text-[11px] text-gray-400 dark:text-slate-400">
                                            <span className="font-semibold text-gray-500 dark:text-slate-300">Instagram Caption Preview</span>
                                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{instagramCopied ? 'Copied to clipboard' : ''}</span>
                                        </div>
                                        <p className="text-xs text-gray-600 dark:text-slate-300 whitespace-pre-line leading-relaxed font-normal bg-gray-50/70 dark:bg-slate-900/70 p-2.5 rounded-lg border border-gray-100 dark:border-slate-800 max-h-24 overflow-y-auto custom-scrollbar">
                                            {getInstagramCaption()}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer Note */}
                        <div className="pt-2 border-t border-gray-100 dark:border-slate-800 flex items-center justify-between text-[11px] text-gray-400 dark:text-slate-500">
                            <span className="truncate max-w-[280px]">Credential: {data.certificate_number}</span>
                            <button
                                onClick={() => setShowShareModal(false)}
                                className="text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-white font-medium cursor-pointer"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StudentSkillProfilePage;
